import { ContextKindKey } from '@gonfalon/contexts/types';
import { enforceResourceNameLength } from '@gonfalon/dogfood-flags/dogfood-flags';
import { fromJS, List, Map, Record, Set } from '@gonfalon/immutable-shim/immutableShim';
import {
  isLength,
  isNotEmpty,
  isReservedKey,
  isValidTagList,
  isValidViewKeysList,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import { findIndexByKey } from '@gonfalon/legacy-immutable/immutableUtils';
import { trackAddTargetsToSegment } from '@gonfalon/segments/analytics';
import { SegmentType } from '@gonfalon/segments/types';
import { isValidKey } from '@gonfalon/strings/isValidKey';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { CheckAccessFunction } from '#utils/access/types';
import type { Clause } from '#utils/clause/records/Clause';
import { createClause } from '#utils/clause/records/Clause';
import type { Member } from '#utils/members/records/Member';

import { SegmentVariationTypes } from '../../expiringContextTargetsUtils';
import { type SegmentProps } from '../types';

import { RelatedFlag } from './RelatedFlag';
import { type SegmentRule, createSegmentRule } from './SegmentRule';
import { SegmentTarget } from './SegmentTarget';

const MAX_SEGMENT_NAME_LENGTH = 256;
const MAX_SEGMENT_KEY_LENGTH = 256;

const validateSegment = (segment: Segment) =>
  validateRecord(
    segment,
    isNotEmpty('name'),
    enforceResourceNameLength() ? isLength(1, MAX_SEGMENT_NAME_LENGTH)('name') : () => undefined,
    isNotEmpty('key'),
    isValidKey('key'),
    isReservedKey('key'),
    isLength(1, MAX_SEGMENT_KEY_LENGTH)('key'),
    isValidTagList('tags'),
    isValidViewKeysList('viewKeys'),
  );

export class Segment extends Record<SegmentProps>({
  _links: Map(),
  _access: undefined,
  _flags: List(),
  _flagCount: 0,
  name: '',
  key: '',
  version: 0,
  description: '',
  tags: Set(),
  viewKeys: Set(),
  creationDate: 0,
  lastModifiedDate: 0,
  rules: List(),
  included: List(),
  excluded: List(),
  includedContexts: List(),
  excludedContexts: List(),
  unbounded: false,
  unboundedContextKind: '',
  _external: '',
  _externalLink: '',
  _unboundedMetadata: {
    version: 0,
    includedCount: 0,
    excludedCount: 0,
    lastModified: 0,
  },
  _importInProgress: false,
}) {
  selfLink() {
    return this._links.getIn(['self', 'href']);
  }

  siteLink() {
    return this._links.getIn(['site', 'href']);
  }

  checkAccess(profile: Member): CheckAccessFunction {
    const access = this._access;

    if (profile.isReader()) {
      return () =>
        createAccessDecision({
          isAllowed: false,
          appliedRoleName: 'Reader',
        });
    }

    if (profile.isAdmin() || profile.isWriter() || profile.isOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  validate() {
    return validateSegment(this);
  }

  addRule(rule?: SegmentRule, atIndex?: number) {
    if (atIndex !== undefined) {
      return this.update('rules', (rs) => rs.insert(atIndex, rule || createSegmentRule()));
    }
    return this.update('rules', (rs) => rs.push(rule || createSegmentRule()));
  }

  deleteRule(rule: SegmentRule) {
    const index = findIndexByKey(this.rules, rule);

    if (index === -1) {
      return this;
    }

    return this.update('rules', (rs) => rs.delete(index));
  }

  addRuleClause(rule: SegmentRule, clause?: Clause) {
    const index = findIndexByKey(this.rules, rule);

    if (index === -1) {
      return this;
    }

    const ruleClauses = this.rules.get(index)?.clauses;

    let contextKind: string = ContextKindKey.USER;

    if (!!ruleClauses && !ruleClauses.isEmpty()) {
      contextKind = ruleClauses.last<Clause>().contextKind;
    }

    return this.updateIn(['rules', index], (r) =>
      r.update('clauses', (cs: List<Clause>) => cs.push(clause || createClause({ contextKind }))),
    );
  }

  deleteRuleClause(rule: SegmentRule, clause: Clause) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const clauseIndex = findIndexByKey(
      this.rules.get(ruleIndex)!.clauses,
      clause,
    ); /* eslint-enable @typescript-eslint/no-non-null-assertion */

    if (clauseIndex === -1) {
      return this;
    }

    return this.updateIn(['rules', ruleIndex], (r) =>
      r.update('clauses', (cs: List<Clause>) => cs.delete(clauseIndex)),
    );
  }

  editRuleClause(rule: SegmentRule, clause: Clause) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const clauseIndex = findIndexByKey(
      this.rules.get(ruleIndex)!.clauses,
      clause,
    ); /* eslint-enable @typescript-eslint/no-non-null-assertion */

    if (clauseIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'clauses', clauseIndex], clause);
  }

  setRuleRollout(rule: SegmentRule, weight: number) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'weight'], weight);
  }

  setRuleRolloutContextKind(rule: SegmentRule, contextKind: string) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'rolloutContextKind'], contextKind);
  }

  setRuleBucket(rule: SegmentRule, attr: string) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'bucketBy'], attr);
  }

  setRuleIndex(ruleKey: string, index: number) {
    return this.update('rules', (rules) => {
      const previousIndex = rules.findIndex((r) => r._key === ruleKey);
      if (previousIndex === -1 || index === -1) {
        return rules;
      }
      const rule = rules.get(previousIndex);
      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      return rules
        .delete(previousIndex)
        .insert(index, rule!); /* eslint-enable @typescript-eslint/no-non-null-assertion */
    });
  }

  setRuleDescription(rule: SegmentRule, description: string) {
    const ruleIndex = findIndexByKey(this.rules, rule);

    if (ruleIndex === -1) {
      return this;
    }

    return this.setIn(['rules', ruleIndex, 'description'], description);
  }

  getTargetCount(segmentVariation: SegmentVariationTypes) {
    let targetValues = 0;
    if (segmentVariation === SegmentVariationTypes.INCLUDED) {
      this.includedContexts.forEach((target) => {
        targetValues += target.values.size;
      });
      targetValues += this.included.size;
    } else {
      this.excludedContexts.forEach((target) => {
        targetValues += target.values.size;
      });
      targetValues += this.excluded.size;
    }
    return targetValues;
  }

  hasTargetsOrRules() {
    return this.countAllTargets() > 0 || this.rules.size > 0;
  }

  setContextTarget(
    targets: List<string>,
    contextKind: string,
    index: number,
    field: 'includedContexts' | 'excludedContexts',
  ) {
    trackAddTargetsToSegment(contextKind);
    // delete the SegmentTarget stored in the included or excluded context field if the list of targets has been cleared for a given context
    if (targets.isEmpty()) {
      if (index >= 0) {
        return this.deleteIn([field, index]);
      } else {
        return this;
      }
    }
    if (index >= 0) {
      // update the list of targets for the given context if it already exists
      return this.updateIn([field, index], (f) => f.set('values', targets));
    } else {
      // create a new SegmentTarget in includedContexts if none already exists for a given context
      const target = new SegmentTarget({ values: targets, contextKind });
      return this.update(field, (f) => f.push(target));
    }
  }

  getContextIndex(variation: 'includedContexts' | 'excludedContexts', contextKind: string) {
    return this[variation].findIndex((ic) => ic.contextKind === contextKind);
  }

  isTargetingNonUserContexts() {
    const hasNonUserTargets = !this.includedContexts.isEmpty() || !this.excludedContexts.isEmpty();
    const hasNonUserTargetingRules = this.rules.some(
      (r) => r.hasNonUserContextClause() || r.hasNonUserContextRollout(),
    );
    return hasNonUserTargets || hasNonUserTargetingRules;
  }

  hasNestedSegmentRule() {
    return this.rules.some((r) => r.hasNestedSegmentClause());
  }

  setIncludedTargets(targets: List<string>, contextKind: string) {
    // user context targets are stored in the included field
    if (contextKind === 'user') {
      trackAddTargetsToSegment(contextKind);
      return this.set('included', targets);
    }
    const index = this.getContextIndex('includedContexts', contextKind);
    return this.setContextTarget(targets, contextKind, index, 'includedContexts');
  }

  setExcludedTargets(targets: List<string>, contextKind: string) {
    // user context targets are stored in the excluded field
    if (contextKind === 'user') {
      trackAddTargetsToSegment(contextKind);
      return this.set('excluded', targets);
    }
    const index = this.getContextIndex('excludedContexts', contextKind);
    return this.setContextTarget(targets, contextKind, index, 'excludedContexts');
  }

  // Combines user and context targets
  getAllTargets(type: SegmentVariationTypes) {
    let contexts = type === SegmentVariationTypes.INCLUDED ? this.includedContexts : this.excludedContexts;
    const userIdx = contexts.findIndex((t) => t.contextKind === ContextKindKey.USER);
    const values = type === SegmentVariationTypes.INCLUDED ? this.included : this.excluded;
    const target = new SegmentTarget({ values, contextKind: ContextKindKey.USER });
    if (userIdx > -1) {
      contexts = contexts.set(userIdx, target);
    } else {
      contexts = contexts.push(target);
    }
    return contexts;
  }

  countAllTargets() {
    if (this.unbounded) {
      const includedCount = this.getIn(['_unboundedMetadata', 'includedCount']);
      const excludedCount = this.getIn(['_unboundedMetadata', 'excludedCount']);
      return includedCount + excludedCount;
    }

    const includedCount = this.getTargetCount(SegmentVariationTypes.INCLUDED);
    const excludedCount = this.getTargetCount(SegmentVariationTypes.EXCLUDED);

    return includedCount + excludedCount;
  }

  countAllRules() {
    return this.rules.size;
  }

  countAllTargetsAndRules() {
    return this.countAllTargets() + this.countAllRules();
  }

  hasDependentFlags() {
    return this._flags.size > 0;
  }

  isBasic() {
    return !this.unbounded;
  }

  isBig() {
    return this.unbounded;
  }

  isNativeBig() {
    return this.isBig() && !this._external;
  }

  isSynced() {
    return this.isBig() && !!this._external;
  }

  updateBigSegmentTargetCount({ addedCount, removedCount }: { addedCount: number; removedCount: number }) {
    if (!this.isNativeBig()) {
      return this;
    }
    const startingCount: number = this.getIn(['_unboundedMetadata', 'includedCount']);
    const updatedCount = startingCount + addedCount - removedCount;
    return this.setIn(['_unboundedMetadata', 'includedCount'], updatedCount);
  }

  isEmptyBigSegment() {
    if (!this.isNativeBig()) {
      return false;
    }
    const hasEmptyTargets = (this.getIn(['_unboundedMetadata', 'includedCount']) as number) === 0;
    return hasEmptyTargets && !this._importInProgress;
  }

  getType() {
    if (this.isSynced() && this._external === SegmentType.AMPLITUDE) {
      return SegmentType.AMPLITUDE;
    }

    if (this.isNativeBig()) {
      return SegmentType.NATIVE_BIG;
    }

    return SegmentType.REGULAR;
  }
}

export function createSegment(props = {}) {
  const segment = props instanceof Segment ? props : new Segment(fromJS(props));
  return segment
    .update('tags', (ts) => ts.toSet())
    .update('rules', (rs) => rs.map(createSegmentRule))
    .update('includedContexts', (ic) => ic?.map((i) => new SegmentTarget(i)))
    .update('excludedContexts', (ec) => ec?.map((e) => new SegmentTarget(e)))
    .update('_flags', (fs: List<RelatedFlag>) => (fs ? fs.map((f) => new RelatedFlag(f)) : fs));
}
